import { useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Search, X } from "lucide-react";
import { useClassNames } from "@/hooks/useClassNames";
import { MenuItem } from "@/types/domain";
import { Sheet, SheetDescription, SheetOverlay, SheetPortal, SheetTitle } from "@/components/shadcn/sheet";
import { useMenuNavigation } from "./useMenuNavigation";

type SettingsPanelProps = {
  settings: MenuItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorLeft: number;
  onCloseAutoFocus?: (event: Event) => void;
}

const matches = (text: string | null | undefined, term: string) => !!text && text.toLowerCase().includes(term);

export const filterCategories = (categories: MenuItem[], filter: string): MenuItem[] => {
  const term = filter.trim().toLowerCase();
  if (!term) return categories;
  return categories
    .map((category) => {
      if (matches(category.label, term) || matches(category.keywords, term)) return category;
      const children = (category.children ?? []).filter((leaf) => matches(leaf.label, term) || matches(leaf.hint, term) || matches(leaf.keywords, term));
      return { ...category, children };
    })
    .filter((category) => (category.children?.length ?? 0) > 0);
}

export const SettingsPanel = ({ settings, open, onOpenChange, anchorLeft, onCloseAutoFocus }: SettingsPanelProps) => {
  const [filter, setFilter] = useState("");
  const { handlePopupClick, handleLinkClick } = useMenuNavigation();
  const categories = useMemo(
    () => (settings.children ?? []).filter((c) => c.isVisible),
    [settings.children],
  );
  const visible = useMemo(() => filterCategories(categories, filter), [categories, filter]);

  const handleOpenChange = (next: boolean) => {
    if (!next) setFilter("");
    onOpenChange(next);
  }

  const closeAfter = () => handleOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetPortal>
        <SheetOverlay
          className="bg-black/20"
          style={{ top: "var(--layout-top-bar-height)", left: anchorLeft }}
        />
        <DialogPrimitive.Content
          aria-describedby="settings-panel-description"
          data-testid="settings-panel"
          onCloseAutoFocus={onCloseAutoFocus}
          className="fixed z-50 flex flex-col bg-white text-slate-900 shadow-2xl w-[340px] max-w-[calc(100vw-48px)] max-lg:w-screen max-lg:max-w-none settings-panel-motion"
          style={{ top: "var(--layout-top-bar-height)", bottom: 0, left: anchorLeft }}
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <SheetTitle className="text-base">Settings</SheetTitle>
            <DialogPrimitive.Close className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close settings">
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <SheetDescription id="settings-panel-description" className="sr-only">
            Browse and filter system settings
          </SheetDescription>
          <div className="px-4 pb-3 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-slate-400" aria-hidden />
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter settings"
                aria-label="Filter settings"
                className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--layout-side-nav-active-bg)]"
                autoFocus
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
            {visible.length === 0 && <p className="px-2 py-6 text-center text-sm text-slate-500">No settings match "{filter}"</p>}
            {visible.map((category) => (
              <section key={category.id} className="mb-3" aria-label={category.label}>
                <h3 className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{category.label}</h3>
                <ul>
                  {category.children?.map((leaf) => (
                    <li key={leaf.id}>
                      <SettingsLeaf leaf={leaf} onActivated={closeAfter} onPopup={handlePopupClick} onLink={handleLinkClick} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </DialogPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}

type SettingsLeafProps = {
  leaf: MenuItem;
  onActivated: () => void;
  onPopup: ReturnType<typeof useMenuNavigation>["handlePopupClick"];
  onLink: ReturnType<typeof useMenuNavigation>["handleLinkClick"];
}

const SettingsLeaf = ({ leaf, onActivated, onPopup, onLink }: SettingsLeafProps) => {
  const className = useClassNames(
    "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100",
    leaf.isSelected && "bg-[var(--layout-side-nav-active-bg)] text-white hover:bg-[var(--layout-side-nav-active-bg)]",
  );
  const content = (
    <>
      <span className="truncate">{leaf.label}</span>
      {leaf.hint && <span className={leaf.isSelected ? "text-xs text-white/80" : "text-xs text-slate-400"}>{leaf.hint}</span>}
    </>
  );

  if (leaf.type === "popup") {
    return (
      <button type="button" className={className} data-testid={leaf.testId} onClick={(e) => { onPopup(e, leaf); onActivated(); }}>
        {content}
      </button>
    );
  }
  return (
    <a href={leaf.url ?? "#"} className={className} data-testid={leaf.testId} onClick={(e) => { onLink(e, leaf); onActivated(); }}>
      {content}
    </a>
  );
}
